import BackButton from "../common/BackButton"
import { Progress } from "@chakra-ui/react"
import { colors } from "../../utilities/colors"
import SwipeAnimation from "../common/SwipeAnimation"
import ImagePreview from "../Dealer/NAP/ImagePreview"
import { useState, useEffect } from "react"
import { NeutralButton } from "../common/Buttons"

export function Wizard({ children }) {
  return (
    <div className="relative flex flex-col grow items-center w-full md:mb-10">
      {children}
    </div>
  )
}

export function WizardHeader({ title, progress, showBackButton = true, handleBack }) {
  return (
    <div className="flex flex-col items-center mb-4 w-full">
      <div className="flex justify-center items-center m-2">
        {showBackButton &&
          <BackButton
            styles="absolute left-3 text-grey-300 text-lg"
            onClick={handleBack}
          />
        }
        <div className="text-grey-300">{title}</div>
      </div>

      <Progress.Root
        size='xs'
        variant='subtle'
        value={progress}
        colorPalette='gray'
        data-testid='progress-bar'
        w='4/5'
        shape='square'
        animated
      >
        <Progress.Track w='full' bgColor={colors.grey700}>
          <Progress.Range bgColor={colors.grey100}/>
        </Progress.Track>
      </Progress.Root>
    </div>
  )
}

export function WizardWorkflow({
  getSteps,
  dependencies = [],
  currentStep = 0,
  setCurrentStep,
  handleNextStep,
  onComplete,
}) {
  const [steps, setSteps] = useState([])

  useEffect(() => {
    const updatedSteps = getSteps()
    setSteps(updatedSteps)

    if (currentStep >= updatedSteps.length) {
      setCurrentStep(updatedSteps.length - 1)
    }
  }, [...dependencies])

  const goToStep = (index) => {
    if (index >= 0 && index < steps.length) {
      setCurrentStep(index)
    }
  }

  const currentStepData = steps[currentStep]

  if (!currentStepData) return null

  const { Component, props = {} } = currentStepData

  return(
    <Component
      {...props}
      nextStep={handleNextStep}
      goToStep={goToStep}
      currentStep={currentStep}
      totalSteps={steps.length}
      onComplete={onComplete}
    />
  )
}

export function WizardStep({ children }) {
  return (
    <SwipeAnimation>
      {children}
    </SwipeAnimation>
  )
}

export function WizardPictureStep({
  title,
  handleSubmit,
  previewable = false,
  image = null,
  nextStep = () => {},
}) {
  const [previewOpen, setPreviewOpen] = useState(false)
  const [imageUrl, setImageUrl] = useState(null)

  useEffect(() => {
    if (image instanceof File) {
      const url = URL.createObjectURL(image)
      setImageUrl(url)
      return () => URL.revokeObjectURL(url)
    } else if (typeof image === 'string') {
      setImageUrl(image)
    } else {
      setImageUrl(null)
    }
  }, [image])

  const onChange = (e) => {
    handleSubmit(e.target.files[0])
    nextStep()
    e.target.value = ''
  }

  return (
    <SwipeAnimation>
      {previewable && (
        <div className="fixed top-0 left-0 z-50">
          {previewOpen && <ImagePreview
            image={imageUrl}
            onClose={() => setPreviewOpen(false)}
          />}
        </div>
      )}

      <div className="flex flex-col items-center justify-between pt-6 rounded w-full h-full">
        <div className="text-2xl mb-4">{title}</div>

        {imageUrl &&
          <div className="flex flex-col items-center mt-6">
            <div
              className="w-full h-[45vh] rounded"
              onClick={() => setPreviewOpen(true)}
              data-testid='photo-preview'
            >
              <img src={imageUrl} alt="Preview" className="w-full h-full object-contain rounded" />
            </div>
          </div>
        }

        <div className="flex flex-col items-center">
          {imageUrl &&
            <NeutralButton
              text="Use Picture"
              onClick={() => {
                handleSubmit(null)
                nextStep()
              }}
              style="text-lg mb-4"
            />
          }

          <input
            type="file"
            accept="image/*"
            capture="camera"
            id="photo-input"
            data-testid="photo-input"
            style={{ display: 'none' }}
            onChange={onChange}
          />
          <label
            htmlFor="photo-input"
            className="flex justify-center items-center text-center text-grey-100 hover:opacity-75 hover:cursor-pointer py-1.5 px-8 border border-2 border-grey-100 rounded-full"
          >
            {imageUrl ? "Retake Picture" : "Take Picture"}
          </label>
        </div>
      </div>
    </SwipeAnimation>
  )
}